import React from 'react';
import { motion } from 'framer-motion';
import { Search, SlidersHorizontal, Users, Building2, BadgeCheck, ChevronDown, Filter, Check, Star, Lock } from 'lucide-react';
import { Counter } from './ui/Counter';
import { Reveal } from './ui/Reveal';
import { TTIco, IGIco, YTIco } from './page/Mockups';
type Creator = {
  name: string;
  photo: string;
  cat: string;
  country: string;
  tt: string;
  ig: string;
  yt: string;
  agency: string;
  excl: 'Exclusive' | 'Non-Exclusive';
  email: string;
};
const CREATORS: Creator[] = [{
  name: 'Michelle Brown',
  photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=64&h=64',
  cat: 'Fashion',
  country: 'United States',
  tt: '2.2M',
  ig: '158.7K',
  yt: '-',
  agency: 'Clicks Talent',
  excl: 'Exclusive',
  email: 'john@clickstalent.com'
}, {
  name: 'Stephanie & Loreal',
  photo: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=64&h=64',
  cat: 'Beauty',
  country: 'United States',
  tt: '169.8K',
  ig: '456.8K',
  yt: '-',
  agency: 'Boost Society',
  excl: 'Exclusive',
  email: 'josh@boost-society.com'
}, {
  name: 'Marcus Chen',
  photo: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=64&h=64',
  cat: 'Tech',
  country: 'United Kingdom',
  tt: '82.9K',
  ig: '38.1K',
  yt: '286M',
  agency: 'Viral Nation',
  excl: 'Non-Exclusive',
  email: 'ena@viralnation.com'
}, {
  name: 'Julius Doyle',
  photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=64&h=64',
  cat: 'Lifestyle',
  country: 'United States',
  tt: '-',
  ig: '449.8K',
  yt: '2.9K',
  agency: 'Connect Management',
  excl: 'Exclusive',
  email: 'oliver@connectmgt.com'
}];
const FILTERS = ['Follower count', 'Average views', 'Engagement rate', 'Pricing', 'Gender', 'Location', 'Platform', 'Niche', 'Tags'];
export const RosterDatabase = () => {
  return <section className="py-24 bg-gray-50 overflow-hidden" data-id="element-1762">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1763">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-16 items-center" data-id="element-1764">
          {/* Copy */}
          <Reveal data-id="element-1765">
            <div data-id="element-1766">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zano-blush text-zano-pink text-xs font-semibold border border-zano-pink/20 mb-5" data-id="element-1767">
                <Users size={13} data-id="element-1768" /> The Roster Database
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-zano-charcoal mb-4 leading-tight" data-id="element-1769">
                Every agency's roster, in one searchable database
              </h2>
              <p className="text-lg text-gray-600 mb-6" data-id="element-1770">
                The first of its kind — a live collection of 400 talent agency
                rosters in one searchable place. Their creators are based mainly
                in the US and UK, with strong representation across other
                European countries, Australia, Canada, the UAE (Dubai), and
                South Africa.
              </p>
              <div className="bg-gray-100/80 border border-gray-200 rounded-lg p-3 mb-4 flex items-start gap-3" data-id="element-1771">
                <div className="p-1.5 bg-white rounded-md shadow-sm shrink-0" data-id="element-1772">
                  <Lock size={14} className="text-gray-500" data-id="element-1773" />
                </div>
                <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1774">
                  <span className="font-semibold text-gray-900" data-id="element-1775">
                    Approved access required.
                  </span>{' '}
                  Anyone can post a casting call to our network of agencies, but
                  viewing the premium roster database and talent agency
                  directory requires an approved brand account.
                </p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-6 flex items-start gap-3" data-id="element-1776">
                <div className="p-1.5 bg-white rounded-md shadow-sm shrink-0" data-id="element-1777">
                  <BadgeCheck size={14} className="text-emerald-600" data-id="element-1778" />
                </div>
                <p className="text-xs text-emerald-900 leading-relaxed" data-id="element-1779">
                  <span className="font-semibold" data-id="element-1780">
                    Every agency is vetted by our team.
                  </span>{' '}
                  Before an agency can create an account, we meet with them
                  one-on-one to verify who they are and the talent they
                  represent — and we check in regularly to keep every roster
                  legitimate and up to date.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-1781">
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-id="element-1782">
                  <p className="text-3xl font-bold text-zano-charcoal" data-id="element-1783">
                    <Counter to={400} suffix="+" data-id="element-1784" />
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-1785">talent agencies</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-id="element-1786">
                  <p className="text-3xl font-bold text-zano-charcoal" data-id="element-1787">
                    <Counter to={6000} suffix="+" data-id="element-1788" />
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-1789">represented creators</p>
                </div>
              </div>

              <ul className="space-y-3" data-id="element-1790">
                <li className="flex items-start gap-3" data-id="element-1791">
                  <div className="mt-0.5 bg-zano-lavender rounded-full p-1 shrink-0" data-id="element-1792">
                    <Building2 size={15} className="text-zano-charcoal" data-id="element-1793" />
                  </div>
                  <span className="text-gray-700 text-sm" data-id="element-1794">
                    <span className="font-semibold text-zano-charcoal" data-id="element-1795">
                      Filter by agency
                    </span>{' '}
                    — already partnering with agencies? Pull up their live
                    rosters instantly to consider talent for a campaign.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1796">
                  <div className="mt-0.5 bg-zano-lavender rounded-full p-1 shrink-0" data-id="element-1797">
                    <BadgeCheck size={15} className="text-zano-charcoal" data-id="element-1798" />
                  </div>
                  <span className="text-gray-700 text-sm" data-id="element-1799">
                    <span className="font-semibold text-zano-charcoal" data-id="element-1800">
                      Exclusively represented only
                    </span>{' '}
                    — work only with exclusively represented creators by
                    filtering for exclusive only.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1801">
                  <div className="mt-0.5 bg-zano-lavender rounded-full p-1 shrink-0" data-id="element-1802">
                    <SlidersHorizontal size={15} className="text-zano-charcoal" data-id="element-1803" />
                  </div>
                  <span className="text-gray-700 text-sm" data-id="element-1804">
                    Filter by follower count, views, engagement, agency-set
                    pricing, gender, location, platform, niche, tags, and
                    free-text keywords.
                  </span>
                </li>
              </ul>
            </div>
          </Reveal>

          {/* Layered visual */}
          <Reveal delay={0.1} data-id="element-1805">
            <div className="relative" data-id="element-1806">
              {/* Glow */}
              <div className="absolute -inset-6 bg-gradient-to-tr from-zano-pink/10 via-zano-lavender/20 to-sky-100/20 rounded-[2.5rem] blur-2xl -z-10" data-id="element-1807" />

              {/* App window */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-float overflow-hidden flex flex-col h-[440px] text-left w-full" data-id="element-1808">
                <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center" data-id="element-1809">
                  <div data-id="element-1810">
                    <h3 className="font-bold text-gray-900 text-base" data-id="element-1811">
                      Roster Database
                    </h3>
                    <p className="text-xs text-gray-500" data-id="element-1812">
                      6,000+ represented creators
                    </p>
                  </div>
                </div>

                <div className="flex-1 overflow-hidden p-4 bg-gray-50" data-id="element-1813">
                  {/* Toolbar */}
                  <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1" data-id="element-1814">
                    <div className="relative flex-1 min-w-[110px]" data-id="element-1815">
                      <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1816" />
                      <input type="text" placeholder="Search" className="w-full pl-6 pr-2 py-1.5 border border-gray-200 rounded-md text-xs outline-none bg-white" data-id="element-1817" />
                    </div>
                    {['Platforms', 'Niche', 'Location'].map(f => <button key={f} className="flex items-center gap-1 border border-gray-200 bg-white px-2.5 py-1.5 rounded-md text-xs text-gray-700 whitespace-nowrap" data-id="element-1818">
                        {f} <ChevronDown size={10} data-id="element-1819" />
                      </button>)}
                    <button className="flex items-center gap-1 border border-zano-pink/30 bg-zano-blush text-zano-pink px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap" data-id="element-1820">
                      <Filter size={12} data-id="element-1821" /> All filters
                    </button>
                  </div>

                  {/* Active filter pills */}
                  <div className="flex flex-wrap gap-1.5 mb-3" data-id="element-1822">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-zano-blush text-zano-pink border border-zano-pink/30" data-id="element-1823">
                      <Building2 size={11} data-id="element-1824" /> Agency: Clicks Talent
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-1825">
                      <BadgeCheck size={11} data-id="element-1826" /> Exclusive only
                    </span>
                  </div>

                  {/* Roster table */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-id="element-1827">
                    <table className="w-full text-left text-xs whitespace-nowrap" data-id="element-1828">
                      <thead className="bg-white border-b border-gray-100 text-gray-500" data-id="element-1829">
                        <tr data-id="element-1830">
                          <th className="px-3 py-2.5 font-medium" data-id="element-1831">Name</th>
                          <th className="px-3 py-2.5 font-medium" data-id="element-1832">
                            Category ↓
                          </th>
                          <th className="px-3 py-2.5 font-medium" data-id="element-1833">Country</th>
                          <th className="px-3 py-2.5 font-medium" data-id="element-1834">
                            <TTIco data-id="element-1835" />
                          </th>
                          <th className="px-3 py-2.5 font-medium" data-id="element-1836">
                            <IGIco data-id="element-1837" />
                          </th>
                          <th className="px-3 py-2.5 font-medium" data-id="element-1838">
                            <YTIco data-id="element-1839" />
                          </th>
                          <th className="px-3 py-2.5 font-medium" data-id="element-1840">Agency</th>
                          <th className="px-3 py-2.5 font-medium" data-id="element-1841">
                            Exclusivity
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50" data-id="element-1842">
                        {CREATORS.map((c, i) => <tr key={i} className="hover:bg-gray-50" data-id="element-1843">
                            <td className="px-3 py-2 flex items-center gap-2" data-id="element-1844">
                              <img src={c.photo} alt={c.name} className="w-6 h-6 rounded-full object-cover shrink-0" data-id="element-1845" />
                              <span className="font-medium text-gray-900 truncate max-w-[100px]" data-id="element-1846">
                                {c.name}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-gray-600" data-id="element-1847">{c.cat}</td>
                            <td className="px-3 py-2 text-gray-600 truncate max-w-[80px]" data-id="element-1848">
                              {c.country}
                            </td>
                            <td className="px-3 py-2 text-gray-600" data-id="element-1849">{c.tt}</td>
                            <td className="px-3 py-2 text-gray-600" data-id="element-1850">{c.ig}</td>
                            <td className="px-3 py-2 text-gray-600" data-id="element-1851">{c.yt}</td>
                            <td className="px-3 py-2 text-gray-700 truncate max-w-[90px]" data-id="element-1852">
                              {c.agency}
                            </td>
                            <td className="px-3 py-2" data-id="element-1853">
                              {c.excl === 'Exclusive' ? <span className="inline-flex items-center gap-1 text-emerald-600 font-medium" data-id="element-1854">
                                  <BadgeCheck size={11} data-id="element-1855" /> {c.excl}
                                </span> : <span className="text-gray-500" data-id="element-1856">{c.excl}</span>}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Floating "All filters" popup layer */}
              <motion.div initial={{
              opacity: 0,
              y: 20,
              x: 20
            }} whileInView={{
              opacity: 1,
              y: 0,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.35
            }} className="absolute -bottom-10 -right-4 sm:-right-10 w-60 bg-white rounded-xl border border-gray-200 shadow-float p-4 z-20" data-id="element-1857">
                <div className="flex items-center gap-2 mb-3" data-id="element-1858">
                  <SlidersHorizontal size={14} className="text-zano-pink" data-id="element-1859" />
                  <p className="text-sm font-semibold text-gray-900" data-id="element-1860">
                    All filters
                  </p>
                </div>

                <div className="space-y-2 mb-3" data-id="element-1861">
                  <label className="flex items-center gap-2 rounded-lg bg-zano-blush/60 border border-zano-pink/20 px-2.5 py-1.5 cursor-pointer" data-id="element-1862">
                    <span className="w-4 h-4 rounded bg-zano-pink flex items-center justify-center" data-id="element-1863">
                      <Check size={11} className="text-white" data-id="element-1864" />
                    </span>
                    <span className="text-xs font-medium text-zano-charcoal flex items-center gap-1" data-id="element-1865">
                      <Building2 size={12} data-id="element-1866" /> By agency
                    </span>
                  </label>
                  <label className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 cursor-pointer" data-id="element-1867">
                    <span className="w-4 h-4 rounded bg-emerald-500 flex items-center justify-center" data-id="element-1868">
                      <Check size={11} className="text-white" data-id="element-1869" />
                    </span>
                    <span className="text-xs font-medium text-emerald-800 flex items-center gap-1" data-id="element-1870">
                      <BadgeCheck size={12} data-id="element-1871" /> Exclusive only
                    </span>
                  </label>
                </div>

                <div className="flex flex-wrap gap-1.5" data-id="element-1872">
                  {FILTERS.map(f => <span key={f} className="px-2 py-0.5 rounded bg-gray-50 border border-gray-200 text-[10px] text-gray-500" data-id="element-1873">
                      {f}
                    </span>)}
                </div>
              </motion.div>

              {/* Floating callout chip */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: 0.6
            }} className="absolute -top-4 -left-3 hidden lg:flex items-center gap-2 bg-white border border-gray-100 shadow-float rounded-full pl-2 pr-3 py-1.5 z-20" data-id="element-1874">
                <span className="w-5 h-5 rounded-full bg-zano-blush flex items-center justify-center" data-id="element-1875">
                  <Star size={11} className="text-zano-pink" data-id="element-1876" />
                </span>
                <span className="text-xs font-semibold text-gray-700 whitespace-nowrap" data-id="element-1877">
                  First of its kind — straight from the source
                </span>
              </motion.div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>;
};